import { CookiesKeys } from '../constants/cookiesKeys';
import getCookieValue from '../utils/getCookieValue';
import setCookieValues from '../utils/setCookieValue';

/**
 * The locales this application ships, in BCP-47, in declaration order.
 *
 * ORDER IS LOAD-BEARING. `normalizeLocale` matches the exact tag first and only
 * then the language subtag, taking the first declaration that matches. `pt-BR`
 * sitting before `pt-PT` is what sends a bare `pt` — and any Portuguese variant
 * this app does not ship — to the Brazilian bundle, the larger audience.
 *
 * THE HYPHEN IS NOT COSMETIC. i18next drops the region when it cannot find a
 * region-specific resource, so with the underscore spelling this app used to
 * carry (`pt_br`, `es_es`, `en_us`) a `pt_pt` resolved to the `pt` bundle —
 * Brazilian Portuguese — and a resource keyed `pt_PT` was never reachable at
 * all. Verified against i18next before this module was written:
 *
 *   resource "pt-PT":  pt_pt → BR   pt-PT → PT   PT-pt → PT   pt → BR
 *   resource "pt_PT":  pt_pt → BR   pt-PT → BR   PT-pt → BR   pt → BR
 *
 * European Portuguese is therefore only expressible in the hyphenated form.
 */
export const LOCALES = ['pt-BR', 'pt-PT', 'en-US', 'es-ES'] as const;

export type AppLocale = (typeof LOCALES)[number];

/** Where the copy is authored, and where everything else falls back to. */
export const DEFAULT_LOCALE: AppLocale = 'pt-BR';

/**
 * Spellings this application itself persisted before the BCP-47 migration.
 *
 * These are NOT hypothetical: the `locale` cookie lives on `.ingresse.com` for a
 * year, and every visitor who passed through the checkout before this change has
 * one. They are also what the language selector wrote, so they can arrive from
 * this app's own history as well as from elsewhere on the platform.
 *
 * `es_uy` is in the list because the currency map in `constants/languages.ts`
 * has always been able to produce it, even though it was never a language this
 * app shipped — it degrades to Spanish rather than to the default.
 */
const LEGACY_ALIASES: Readonly<Record<string, AppLocale>> = {
  pt_br : 'pt-BR',
  pt_pt : 'pt-PT',
  en_us : 'en-US',
  es_es : 'es-ES',
  es_uy : 'es-ES',
};

/**
 * Canonicalizes any spelling the platform might hand us into a locale this app
 * ships, or `null` when nothing matches so the caller decides the fallback.
 *
 * Handles, in order: the legacy underscore aliases above, the canonical tag in
 * any casing (`PT-pt`), and a bare language subtag (`pt`).
 */
export function normalizeLocale(tag: string | null | undefined): AppLocale | null {
  if (typeof tag !== 'string') return null;

  const trimmed = tag.trim();
  if (!trimmed) return null;

  const alias = LEGACY_ALIASES[trimmed.toLowerCase()];
  if (alias) return alias;

  const [language, region] = trimmed.replace(/_/g, '-').split('-');
  if (!language) return null;

  const canonical = region
    ? `${language.toLowerCase()}-${region.toUpperCase()}`
    : language.toLowerCase();

  const exact = LOCALES.find((locale) => locale === canonical);
  if (exact) return exact;

  return LOCALES.find((locale) => locale.split('-')[0] === language.toLowerCase()) ?? null;
}

/**
 * The raw value of the shared `locale` cookie, or an empty string.
 *
 * Goes through `getCookieValue` rather than reading `document.cookie`: inside
 * the cross-site iframe this app runs in, the browser silently drops the cookie
 * and the value only survives in the credentials mirror. Reading the cookie
 * directly would work on the standalone checkout and quietly return nothing on
 * the embed — which is the surface most visitors are on.
 */
export function readLocaleCookie(): string {
  return getCookieValue({ cookieKey : CookiesKeys.LOCALE, needPrefix : false });
}

/**
 * How long the shared language preference is meant to live.
 *
 * A year, matching what `@ingresse/awa-sdk-web` writes from the AWA
 * applications. This app used to take the `setCookieValues` default of ONE DAY,
 * so simply passing through the checkout shortened the platform-wide preference
 * to 24 hours — the visitor picked a language in the authenticator and lost it
 * the next day without ever touching the setting.
 */
const LOCALE_COOKIE_DAYS = 365;

/**
 * Persists the choice on `.ingresse.com`, so it survives the jump between
 * Ingresse applications.
 *
 * Writes through `setCookieValues`, which also mirrors into credentials storage
 * for the cross-site iframe case.
 */
export function writeLocaleCookie(locale: AppLocale): void {
  setCookieValues(
    [{ key : CookiesKeys.LOCALE, value : locale }],
    false,
    LOCALE_COOKIE_DAYS,
  );
}

/**
 * The locale this session should render in, from the shared cookie.
 *
 * Returns `null` when the cookie says nothing usable, so the caller can fall
 * through to the query parameter and then to the device — that ordering belongs
 * to the provider, not here.
 */
export function resolveStoredLocale(): AppLocale | null {
  return normalizeLocale(readLocaleCookie());
}
